import React, { useEffect, useState } from "react";
import { createOtaArtifact, parseOtaArtifactZip } from "../../commonApi";
import { extractErrorMessage, extractSuccessMessage } from "../../Utils/ExtractMessages";
import { toast } from "react-toastify";
import { getArtifactFormFields, MAX_ARTIFACT_FILE_SIZE_BYTES } from "./ArtifactFormFieldsConfig";
import { generateMD5Checksum } from "../../Utils/ChecksumGeneration";
import DynamicForm from "../../components/DynamicForm";
import { Loader } from "../../Loader";

const formatVersion = (version) => {
    if (!version) return "";
    const { major = 0, minor = 0, patch = 0 } = version;
    return `${major}.${minor}.${patch}`;
};

const formatHwVersion = (hwVersion) => {
    if (!hwVersion) return "";
    const { major = 0, minor = 0 } = hwVersion;
    return `${major}.${minor}`;
};

// buildTimeFormatted comes as "YYYY-MM-DD HH:mm:ss <ZONE>" — drop the trailing zone token.
const formatBuildTime = (buildTimeFormatted) => {
    if (!buildTimeFormatted) return "";
    const [date, time] = buildTimeFormatted.split(" ");
    return date && time ? `${date} ${time}` : buildTimeFormatted;
};

export const CreateArtifactModal = ({
    isOpen,
    onClose,
    deviceModels,
    onSuccess
}) => {
    const [parsedData, setParsedData] = useState(null);
    const [isParsing, setIsParsing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setParsedData(null);
            setIsParsing(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const applyParsedValues = (data, setValue) => {
        setValue("versionString", data.versionString || "");
        setValue("otaType", data.otaType || "");
        setValue("headerVersion", data.headerVersion ?? "");
        setValue("buildTime", formatBuildTime(data.buildTimeFormatted));
        setValue("vendorId", data.vendorId || "");
        setValue("productId", data.productId || "");
        setValue("minRequiredVersion", formatVersion(data.minRequiredVersion));
        setValue("minHwVersion", formatHwVersion(data.minHwVersion));
        setValue("payloadLengthMb", data.payloadLengthMb || "");
        setValue("payloadMd5", data.payloadMd5 || "");

        if (data.resolvedDeviceModel) {
            setValue("deviceModelId", {
                label: data.resolvedDeviceModel.name,
                value: data.resolvedDeviceModel.id,
            });
        }
    };

    const clearParsedFields = (setValue) => {
        setParsedData(null);
        setValue("deviceModelId", "");
        setValue("fileSizeBytes", "");
        setValue("checksum", "");
        setValue("versionString", "");
        setValue("otaType", "");
        setValue("headerVersion", "");
        setValue("buildTime", "");
        setValue("vendorId", "");
        setValue("productId", "");
        setValue("minRequiredVersion", "");
        setValue("minHwVersion", "");
        setValue("payloadLengthMb", "");
        setValue("payloadMd5", "");
    };

    const handleFileChange = async (file, setValue, getValues, inputEl) => {
        clearParsedFields(setValue);
        if (!file) return;

        if (file.size > MAX_ARTIFACT_FILE_SIZE_BYTES) {
            toast.error(`File exceeds the maximum allowed size of ${MAX_ARTIFACT_FILE_SIZE_BYTES / (1024 * 1024)} MB.`);
            setValue("file", null);
            if (inputEl) inputEl.value = "";
            return;
        }

        setValue("fileSizeBytes", file.size);

        const checksum = await generateMD5Checksum(file);
        setValue("checksum", checksum);

        setIsParsing(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await parseOtaArtifactZip(formData);
            const data = response.data.data;

            setParsedData(data);
            applyParsedValues(data, setValue);
            toast.success("Package parsed successfully — fields auto-filled.");
        } catch (error) {
            toast.error(extractErrorMessage(error));
        } finally {
            setIsParsing(false);
        }
    };

    const fields = getArtifactFormFields(deviceModels, handleFileChange, "create", parsedData);

    const readOnlyFieldNames = [
        "deviceModelId", "fileSizeBytes", "checksum",
        "versionString", "otaType", "headerVersion", "buildTime",
        "vendorId", "productId", "minRequiredVersion", "minHwVersion",
        "payloadLengthMb", "payloadMd5",
    ];

    const handleSubmit = async (values) => {
        try {
            const artifactPayload = {
                deviceModelId: values.deviceModelId?.value,
                artifactType: values.artifactType?.value,
                releaseChannel: values.releaseChannel?.value,
                artifactName: values.artifactName,
                fileSizeBytes: values.fileSizeBytes,
                checksum: values.checksum,
                checksumAlgo: "MD5",
                isConsentRequired: values.isConsentRequired || false,
            };

            const formData = new FormData();
            formData.append(
                "artifact",
                new Blob([JSON.stringify(artifactPayload)], { type: "application/json" })
            );
            formData.append("file", values.file);

            const response = await createOtaArtifact(formData);

            toast.success(extractSuccessMessage(response, "Artifact created successfully"));

            onClose();
            onSuccess();
        } catch (error) {
            toast.error(extractErrorMessage(error));
        }
    };

    return (
        <div className="artifact-modal-overlay">
            <div className="artifact-modal-container">
                <div className="artifact-modal-header" style={{ color: "#4340d2" }}>
                    <h4>Create Artifact</h4>
                    <button type="button" className="btn-close" onClick={onClose} />
                </div>

                <div className="artifact-modal-body" style={{ overflowY: isParsing ? "hidden" : "auto" }}>
                    {isParsing && (
                        <div className="artifact-parsing-overlay">
                            <Loader />
                            <p>Parsing OTA package...</p>
                        </div>
                    )}
                    <div style={{ visibility: isParsing ? "hidden" : "visible" }}>
                        <DynamicForm
                            formClass="artifact-form"
                            fields={fields}
                            onSubmit={handleSubmit}
                            onClose={onClose}
                            disabledFields={readOnlyFieldNames}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
